import { Button } from '@dev-spendesk/grapes';
import { useEffect } from 'react';

import { useTranslation } from 'src/core/common/hooks/useTranslation';
import {
  NotificationType,
  useNotifications,
} from 'src/core/modules/app/notifications';

import { useCostCenterPolling } from '../../hooks/useCostCenterPolling';
import {
  type CostCenterReplacePreview,
  useCostCenterReplacePreviewByCompany,
} from '../../hooks/useCostCenterReplacePreviewByCompany';
import { useDeleteOrganisationCostCenter } from '../../hooks/useDeleteOrganisationCostCenter';
import { type Setting } from '../../setting';
import { CommonModal } from '../CommonModal';
import { CostCenterReassignmentModal } from '../CostCenterReassignmentModal';

export const CostCenterDeleteModal = ({
  settings,
  isOpen,
  onCancel,
}: {
  settings: Setting[];
  isOpen: boolean;
  onCancel: () => void;
}) => {
  const { t } = useTranslation('global');
  const { pushNotif } = useNotifications();
  const { mutate: deleteCostCenter } = useDeleteOrganisationCostCenter();
  const {
    data: aggregatedPreviews,
    isLoading,
    isError,
  } = useCostCenterReplacePreviewByCompany(settings);
  const { isSubmitting, startPolling, stopPolling } = useCostCenterPolling({
    type: 'delete',
    settings,
    onSuccess: onCancel,
  });

  useEffect(() => {
    if (!isOpen) {
      stopPolling();
    }
  }, [isOpen, stopPolling]);

  if (isLoading || isError || !aggregatedPreviews) {
    return null;
  }

  const companiesCostCentersToReplace: CostCenterReplacePreview[] = [];
  aggregatedPreviews.forEach((p) => {
    if (Object.values(p.preview).some((count) => count > 0)) {
      companiesCostCentersToReplace.push(p);
    }
  });

  const hasCostCentersToReplace = companiesCostCentersToReplace.length > 0;

  const handleDelete = async () => {
    try {
      await deleteCostCenter(settings);
      startPolling();
    } catch (error) {
      stopPolling();
      pushNotif({
        type: NotificationType.Danger,
        message: t('multiEntityHub.costCenters.messages.errorDeleteCostCenter'),
      });
      throw error;
    }
  };

  const replaceCostCenterModal = (
    <CostCenterReassignmentModal
      settings={settings}
      iconVariant="alert"
      title={t(
        settings.length === 1
          ? 'multiEntityHub.costCenters.reassign.title'
          : 'multiEntityHub.costCenters.reassign.bulkTitle',
        {
          costCenterName: settings.length === 1 ? settings[0].name : '',
          count: companiesCostCentersToReplace.length,
        },
      )}
      subtitle={t(
        settings.length === 1
          ? 'multiEntityHub.costCenters.reassign.description'
          : 'multiEntityHub.costCenters.reassign.bulkDescription',
        {
          costCenterName: settings.length === 1 ? settings[0].name : '',
          count: companiesCostCentersToReplace.length,
        },
      )}
      iconName="trash"
      submitButtonText={t(
        'multiEntityHub.costCenters.reassign.actions.confirm',
      )}
      submitButtonVariant="primaryAlert"
      companiesCostCentersToReplace={companiesCostCentersToReplace}
      onConfirm={() => !isSubmitting && handleDelete()}
      onCancel={() => {
        onCancel();
      }}
    />
  );

  const deleteCostCenterModal = (
    <CommonModal
      isOpen={isOpen}
      title={
        settings.length === 1
          ? t('multiEntityHub.costCenters.modal.delete.title')
          : t('multiEntityHub.costCenters.modal.delete.bulkTitle', {
              count: settings.length,
            })
      }
      subtitle={
        settings.length === 1
          ? t('multiEntityHub.costCenters.modal.delete.description')
          : t('multiEntityHub.costCenters.modal.delete.bulkDescription', {
              count: settings.length,
            })
      }
      iconName="pen"
      onClose={onCancel}
    >
      <div className="-mb-24 flex w-full justify-center gap-16">
        <Button
          variant="secondaryNeutral"
          onClick={onCancel}
          text={t('multiEntityHub.costCenters.modal.delete.actions.cancel')}
          disabled={isSubmitting}
        />
        <Button
          variant="primaryAlert"
          className={isSubmitting ? 'cursor-progress' : ''}
          onClick={() => !isSubmitting && handleDelete()}
          text={t('multiEntityHub.costCenters.modal.delete.actions.confirm')}
          isLoading={isSubmitting}
        />
      </div>
    </CommonModal>
  );

  return hasCostCentersToReplace
    ? replaceCostCenterModal
    : deleteCostCenterModal;
};
